import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * ChatContext exposes a global `open()` / `close()` API for the Square Bot
 * chat panel. `open()` is idempotent — calling it while the panel is already
 * open simply bumps `focusTick` so the panel can re-focus its input.
 */
const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [focusTick, setFocusTick] = useState(0);

  const open = useCallback(() => {
    setOpen(true);
    setFocusTick((t) => t + 1);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setOpen((v) => !v);
    setFocusTick((t) => t + 1);
  }, []);

  const value = useMemo(
    () => ({ isOpen, focusTick, open, close, toggle }),
    [isOpen, focusTick, open, close, toggle]
  );
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within <ChatProvider>");
  return ctx;
};
